import { useEffect } from 'react';
import { GoSell } from '@tap-payments/gosell';
import { useTranslation } from 'react-i18next';
import EmptyPlaceholder from '../../../components/empty-placholder';
import usePaymentMethods from '../../../hooks/use-payment-methods';
import emptyCardsPlaceholder from '../../../assets/images/empty-cards.svg';
import PaymentMethodCard from './payment-method-card';
import TamaraPaymentOption from './tamara-payment-option';

interface TamaraOptions {
  description: string;
  name: string;
}
interface PaymentMethodsProps {
  setSelectedCardId: (id: string | null) => void;
  selectedCardId: string | null;
  tamaraOptions: TamaraOptions[];
}

export default function PaymentMethods({
  setSelectedCardId,
  selectedCardId,
  tamaraOptions,
}: PaymentMethodsProps) {
  const {
    fetchPaymentMethods,
    loadingCards,
    loadingTapConfig,
    loadingTamara,
    paymentMethods,
    fetchTapConfigs,
    tapPublicKey,
    tapUserDetails,
  } = usePaymentMethods();

  const { t } = useTranslation();

  useEffect(() => {
    fetchPaymentMethods();
    fetchTapConfigs();
  }, []);

  const callbackFunc = (response: any) => {
    if (response) {
      fetchPaymentMethods();
    }
  };

  const showAddCardButton = !!tapPublicKey && !!tapUserDetails;

  const hasPaymentOptions = (paymentMethods?.length ?? 0) > 0 || (tamaraOptions?.length ?? 0) > 0;

  return (
    <div className='pb-6'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-lg font-medium text-black'>{t('account.paymentMethods.title')}</h2>
        {showAddCardButton && (
          <button
            type='button'
            className='rounded-md bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-lime-600 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600'
            onClick={() => GoSell.openLightBox()}
            disabled={!tapPublicKey || !tapUserDetails}
          >
            {t('account.paymentMethods.addCard')}
          </button>
        )}
      </div>
      {loadingCards || loadingTapConfig || loadingTamara ? (
        <div className='grid grid-cols-1 gap-4'>
          {[...Array(2)].map((item) => (
            <div key={item} className='w-full h-20 bg-gray-300 rounded-md animate-pulse' />
          ))}
        </div>
      ) : !hasPaymentOptions && !showAddCardButton ? (
        <EmptyPlaceholder
          title={t('account.paymentMethods.savedCards.noCardsMessage')}
          subtitle=''
          imageSrc={emptyCardsPlaceholder}
          fullHeight={false}
        />
      ) : (
        <div className='grid grid-cols-1 gap-4'>
          {paymentMethods.map((paymentMethod) => (
            <PaymentMethodCard
              key={paymentMethod.id}
              data={paymentMethod}
              onSelect={() => setSelectedCardId(paymentMethod.id)}
              selectedCardId={selectedCardId}
            />
          ))}
          {tamaraOptions.map((option) => (
            <TamaraPaymentOption
              key={option.name}
              name={option.name}
              description={option.description}
              isSelected={selectedCardId === option.name}
              onSelect={() => setSelectedCardId(option.name)}
            />
          ))}
        </div>
      )}
      {tapPublicKey && tapUserDetails && (
        <GoSell
          gateway={{
            publicKey: tapPublicKey,
            language: 'en',
            supportedCurrencies: ['SAR'],
            supportedPaymentMethods: 'all',
            callback: callbackFunc,
          }}
          customer={tapUserDetails}
          transaction={{
            mode: 'save_card',
          }}
        />
      )}
    </div>
  );
}
